import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import Axios from "axios";

import { convertDecimal, formatData } from "../../../../util/targetCalc";
import GYAxios from "../../../../util/GYAxios";
import Noti from "../../../../util/noti";

import TargetAsbuiltSection from "./TargetAsbuiltSection";
import TargetCorrSection from "./TargetCorrSection";
import TargetDegradeSection from "./TargetDegradeSection";
import TargetEditSection from "./TargetEditSection";
import TargetEgridSection from "./TargetEgridSection";
import TargetFileSection from "./TargetFileSection";
import TargetIrrSection from "./TargetIrrSection";
import TargetSubmitSection from "./TargetSubmitSection";
import TargetTempSection from "./TargetTempSection";
import TargetUnusedSection from "./TargetUnusedSection";

const TargetForm = (props) => {
	const [data, setData] = useState({});
	const { register, handleSubmit, errors, reset, watch, getValues, setValue } =
		useForm({ defaultValues: data });
	const [totalEGrid, setTotalEGrid] = useState(0);
	const [avgUnused, setAvgUnused] = useState(0);
	const [totalCorr, setTotalCorr] = useState(0);
	const [isEditMode, setIsEditMode] = useState(false);
	const [selectedSite, setSelectedSite] = useState(props.selectedSite);

	const setReload = props.setReload;

	const inputNames = [
		"net_yield",
		"net_yield_un",
		"BP_yield",
		"pvsyst_prod",
		"unused_avg",
		"TA_correction",
		"GA_correction",
		"other_corrections",
		"degradation",
		"january_irr",
		"february_irr",
		"march_irr",
		"april_irr",
		"may_irr",
		"june_irr",
		"july_irr",
		"august_irr",
		"september_irr",
		"october_irr",
		"november_irr",
		"december_irr",
		"january_unused",
		"february_unused",
		"march_unused",
		"april_unused",
		"may_unused",
		"june_unused",
		"july_unused",
		"august_unused",
		"september_unused",
		"october_unused",
		"november_unused",
		"december_unused",
		"january_temp",
		"february_temp",
		"march_temp",
		"april_temp",
		"may_temp",
		"june_temp",
		"july_temp",
		"august_temp",
		"september_temp",
		"october_temp",
		"november_temp",
		"december_temp",
		"january_egrid",
		"february_egrid",
		"march_egrid",
		"april_egrid",
		"may_egrid",
		"june_egrid",
		"july_egrid",
		"august_egrid",
		"september_egrid",
		"october_egrid",
		"november_egrid",
		"december_egrid",
	];

	/**
	 * Fetch target data of selected site
	 * @param {obj} selectedSite
	 */
	const fetchData = (selectedSite) => {
		const siteID = selectedSite.site_id;
		const filterStr = `filter?column=id&value=${siteID}`;

		// API request to get target data
		const paramReq = GYAxios.get(`/target/parameters/${filterStr}`);
		const irrReq = GYAxios.get(`/target/irradiance/${filterStr}`);
		const unusedReq = GYAxios.get(`/target/unused/${filterStr}`);
		const tempReq = GYAxios.get(`/target/temperature/${filterStr}`);
		const egridReq = GYAxios.get(`/target/egrid/${filterStr}`);

		const reqArr = [paramReq, irrReq, unusedReq, tempReq, egridReq];

		Axios.all(reqArr).then(
			Axios.spread((...responses) => {
				const paramRes = responses[0];
				const irrRes = responses[1];
				const unusedRes = responses[2];
				const tempRes = responses[3];
				const egridRes = responses[4];

				const paramObj = paramRes.error == null ? paramRes.data[0] : null;
				const irrObj = irrRes.error == null ? irrRes.data[0] : null;
				const unusedObj = unusedRes.error == null ? unusedRes.data[0] : null;
				const tempObj = tempRes.error == null ? tempRes.data[0] : null;
				const egridObj = egridRes.error == null ? egridRes.data[0] : null;

				if (
					paramObj != null &&
					irrObj != null &&
					unusedObj != null &&
					egridObj != null &&
					tempObj != null
				) {
					const formattedData = formatData(
						paramObj,
						irrObj,
						unusedObj,
						tempObj,
						egridObj
					);

					setData(formattedData);
					reset();
					setReload(false);
				}
			})
		);
	};

	/**
	 * Handle form submit
	 * @param {obj} data
	 */
	const onSubmit = (submittedData) => {
		const id = data.id;
		delete data.table_name;
		const formattedData = handleSubmittedInputs(submittedData);
		submittedData = { id, ...submittedData, ...formattedData };

		const resultObj = compareData(data, submittedData);
		prepareSubmit({ id, ...resultObj });
	};

	/**
	 * Prepare to submit data via API
	 * @param {obj} resultObj
	 */
	const prepareSubmit = (resultObj) => {
		const entries = Object.entries(resultObj);

		const paramEntries = [];
		const irrEntries = [];
		const unusedEntries = [];
		const tempEntries = [];
		const egridEntries = [];

		for (let i = 0; i < entries.length; i++) {
			const key = entries[i][0];
			switch (true) {
				case usesParamReq(key):
					paramEntries.push(entries[i]);
					break;
				case usesIrrReq(key):
					irrEntries.push(entries[i]);
					break;
				case usesUnusedReq(key):
					unusedEntries.push(entries[i]);
					break;
				case usesTempReq(key):
					tempEntries.push(entries[i]);
					break;
				case usesEgridReq(key):
					egridEntries.push(entries[i]);
					break;

				default:
					break;
			}
		}

		const reqArray = [];
		const inputNameArray = [];
		const siteID = resultObj.id;

		const paramReq = prepareRequest(
			paramEntries,
			`/target/parameters/site/${siteID}`
		);
		const irrReq = prepareRequest(
			irrEntries,
			`/target/irradiance/site/${siteID}`
		);
		const unusedReq = prepareRequest(
			unusedEntries,
			`/target/unused/site/${siteID}`
		);
		const tempReq = prepareRequest(
			tempEntries,
			`/target/temperature/site/${siteID}`
		);
		const egridReq = prepareRequest(
			egridEntries,
			`/target/egrid/site/${siteID}`
		);

		if (paramReq) {
			reqArray.push(paramReq);
			inputNameArray.push("parameters");
		}
		if (irrReq) {
			reqArray.push(irrReq);
			inputNameArray.push("irradiance");
		}
		if (unusedReq) {
			reqArray.push(unusedReq);
			inputNameArray.push("unused");
		}
		if (tempReq) {
			reqArray.push(tempReq);
			inputNameArray.push("temperature");
		}
		if (egridReq) {
			reqArray.push(egridReq);
			inputNameArray.push("egrid");
		}

		executeUpdate(reqArray, inputNameArray);
	};

	/**
	 * Prepare update request
	 * @param {array} entries
	 * @param {string} URI
	 * @returns
	 */
	const prepareRequest = (entries, URI) => {
		const splitURI = URI.split("/");
		const usesParamAPI = splitURI[2] === "parameters";

		const formattedEntries = !usesParamAPI
			? entries.map((entry) => [entry[0].split("_")[0], entry[1]])
			: entries;

		const data =
			formattedEntries.length > 0
				? Object.fromEntries(formattedEntries)
				: false;
		const req = data ? GYAxios.patch(URI, data) : false;

		return req;
	};

	/**
	 * Execute update requests
	 * @param {array} reqArray
	 * @param {array} inputNameArray
	 */
	const executeUpdate = (reqArray, inputNameArray) => {
		if (reqArray.length > 0) {
			Axios.all(reqArray).then(
				Axios.spread((...responses) => {
					const successRes = responses.map((response, i) => {
						const resObj = response.data;
						if (resObj.error == null && resObj.affectedRows > 0)
							return inputNameArray[i];
						else return null;
					});

					const failedRes = responses.map((response, i) => {
						const resObj = response.data;
						if (resObj.error != null) return inputNameArray[i];
						else return null;
					});

					const filteredSuccess = successRes.filter((res) => res != null);
					const filteredFailed = failedRes.filter((res) => res != null);

					handleNotification(filteredSuccess, filteredFailed, responses.length);
				})
			);
		} else Noti.create({ type: "w", msg: "No Change" });
	};

	/**
	 * Check if inputs uses parameters request
	 * @param {string} name
	 * @returns boolean
	 */
	const usesParamReq = (name) =>
		[
			"net_yield",
			"net_yield_un",
			"BP_yield",
			"pvsyst_prod",
			"unused_avg",
			"TA_correction",
			"GA_correction",
			"other_corrections",
			"degradation",
		].includes(name);

	/**
	 * Check if inputs uses irradiance update request
	 * @param {string} name
	 * @returns boolean
	 */
	const usesIrrReq = (name) => name.includes("irr");
	/**
	 * Check if inputs uses unused update request
	 * @param {string} name
	 * @returns boolean
	 */

	const usesUnusedReq = (name) => name.includes("unused");
	/**
	 * Check if inputs uses temperature update request
	 * @param {string} name
	 * @returns boolean
	 */
	const usesTempReq = (name) => name.includes("temp");
	/**
	 * Check if inputs uses temperature update request
	 * @param {string} name
	 * @returns boolean
	 */
	const usesEgridReq = (name) => name.includes("egrid");

	/**
	 * Handle submitted values
	 * @param {obj} submittedData
	 * @returns formatted submitted values
	 */
	const handleSubmittedInputs = (submittedData) => {
		const entries = Object.entries(submittedData);
		const corrSection = "correction";
		const unusedSection = "unused";
		const degradeSection = "degradation";

		// Filter percentage inputs
		const percentageInputs = entries.filter(
			(entry) =>
				entry[0].includes(corrSection) ||
				entry[0].includes(unusedSection) ||
				entry[0].includes(degradeSection)
		);

		// Convert from percentage to decimals
		const convertedEntries = percentageInputs.map((input) => [
			input[0],
			convertDecimal(input[1]),
		]);

		const dataObj = Object.fromEntries(convertedEntries);

		return dataObj;
	};

	/**
	 * Compare and get modified values
	 * @param {obj} data
	 * @param {obj} submittedData
	 * @returns Modified inputs object
	 */
	const compareData = (data, submittedData) => {
		// Filter input names with modified values
		const modifiedValueNames = inputNames.filter(
			(name) => data[name] != submittedData[name]
		);

		const entries = Object.entries(submittedData);

		// Filter key-value pair of modified value
		const resultEntry = entries.filter((entry) =>
			modifiedValueNames.includes(entry[0])
		);

		// Parse values to float
		const parseValues = resultEntry.map((entry) => [
			entry[0],
			parseFloat(entry[1]),
		]);

		const resultObj = Object.fromEntries(parseValues);

		return resultObj;
	};

	/**
	 * Handle notifications based on update response
	 * @param {obj} successRes
	 * @param {obj} failedRes
	 * @param {obj} size
	 */
	const handleNotification = (successRes, failedRes, size) => {
		// All updated successfully
		if (successRes.length === size) {
			Noti.create({
				type: "s",
				msg: "Info Updated!",
			});

			setIsEditMode(false);
			setReload(true);
		}
		// All update failed
		else if (failedRes.length === size) {
			Noti.create({
				type: "e",
				msg: "Something went wrong! Please contact IT",
			});
		}
		// Some updated successful and failed
		else {
			const successStr = successRes.join(",");
			const failedStr = failedRes.join(",");

			Noti.create({
				type: "s",
				msg: `Sections Updated: \n ${successStr}`,
			});

			Noti.create({
				type: "e",
				msg: `Sections Failed: \n ${failedStr}`,
			});

			setIsEditMode(false);
			setReload(true);
		}
	};

	useEffect(() => {
		if (props.selectedSite != null) setSelectedSite(props.selectedSite);
	}, [props.selectedSite]);

	useEffect(() => {
		if (props.reload) fetchData(selectedSite);
	}, [props.reload]);

	return (
		<div>
			<Form onSubmit={handleSubmit(onSubmit)} id="target-inputs">
				<TargetEditSection
					isEditMode={isEditMode}
					setIsEditMode={setIsEditMode}
					setReload={setReload}
				/>
				{isEditMode && (
					<TargetFileSection
						isEditMode={isEditMode}
						data={data}
						setTotalEGrid={setTotalEGrid}
						setAvgUnused={setAvgUnused}
						setValue={setValue}
						setReload={setReload}
					/>
				)}
				<TargetAsbuiltSection
					avgUnused={avgUnused}
					capacity={selectedSite.power}
					data={data}
					errors={errors}
					isEditMode={isEditMode}
					register={register({ required: true })}
					setValue={setValue}
					totalCorr={totalCorr}
					totalEGrid={totalEGrid}
					watch={watch}
				/>
				<TargetCorrSection
					data={data}
					errors={errors}
					isEditMode={isEditMode}
					register={register({ required: true })}
					setTotalCorr={setTotalCorr}
					setValue={setValue}
					watch={watch}
				/>
				<TargetEgridSection
					data={data}
					errors={errors}
					getValues={getValues}
					isEditMode={isEditMode}
					register={register({ required: true })}
					selectedSite={selectedSite}
					setTotalEGrid={setTotalEGrid}
					setValue={setValue}
					watch={watch}
				/>
				<TargetUnusedSection
					data={data}
					errors={errors}
					isEditMode={isEditMode}
					register={register({ required: true })}
					setAvgUnused={setAvgUnused}
					setValue={setValue}
					watch={watch}
				/>
				<TargetIrrSection
					data={data}
					errors={errors}
					isEditMode={isEditMode}
					register={register({ required: true })}
					setValue={setValue}
					watch={watch}
				/>
				<TargetTempSection
					data={data}
					errors={errors}
					isEditMode={isEditMode}
					register={register({ required: true })}
					setValue={setValue}
					watch={watch}
				/>
				<TargetDegradeSection
					data={data}
					errors={errors}
					isEditMode={isEditMode}
					register={register({ required: true })}
					setValue={setValue}
					watch={watch}
				/>
				{isEditMode && (
					<TargetSubmitSection isEditMode={isEditMode} setReload={setReload} />
				)}
			</Form>
			<Noti />
		</div>
	);
};

export default TargetForm;
